const ADD_ITEM_TO_CART = "ADD_ITEM_TO_CART";
const REMOVE_ITEM_FROM_CART = "REMOVE_ITEM_FROM_CART";
const CLEAR_CART = "CLEAR_CART";
const SET_FORWARD_URL = "SET_FORWARD_URL";
const SET_TOTAL_WITH_INTEREST = "SET_TOTAL_WITH_INTEREST";

const LOCAL_STORAGE_KEY = "_$CART";

function initialState() {
  return {
    items: localStorage.getItem(LOCAL_STORAGE_KEY)
      ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY))
      : [],
    totalWithInterest: 0.0,
    forwardUrl: null,
  };
}

const relativeShoppingCartStore = {
  namespaced: true,

  state: initialState,

  getters: {
    continueToPaymentButtonEnabled(state) {
      return state.items.length > 0;
    },

    cartCount(state) {
      return state.items.length;
    },

    showTaxes(state) {
      return state.items.some((item) =>
        item.type == "program"
          ? item.data.program.childPrices.hasTax === false
          : item.data.hasTax === false
      );
    },

    cartTaxes(state) {
      return state.items
        .map((el) => {
          if (el.type == "program") {
            return el.data.program.childPrices.tax;
          }

          if (el.type == "article") {
            return el.data.tax * el.quantity;
          }

          return 0;
        })
        .reduce((a, b) => a + b, 0);
    },

    cartSubtotal(state) {
      const total = state.items.map((el) => {
        if (el.type == "article") {
          return el.quantity * el.data.subtotal;
        }

        const {
          userDiscount,
          programPrice: price,
          programEarlyPrice,
          applyBasePriceEndDate,
          tax,
          hasTax,
        } = el.data.program.childPrices;

        let total = hasTax === false ? price : price - tax;

        if (applyBasePriceEndDate === false) {
          total = programEarlyPrice;
        }

        if (userDiscount) {
          total = userDiscount.price - tax;
        }

        return total;
      });

      return total.reduce((a, b) => a + b, 0);
    },

    cartTotal(state) {
      const total = state.items.map((el) => {
        if (el.type == "article") {
          return el.quantity * el.data.price;
        }

        const {
          hasTax,
          tax,
          userDiscount,
          programPrice: price,
          programEarlyPrice,
          applyBasePriceEndDate,
        } = el.data.program.childPrices;

        let total = hasTax === false ? price + tax : price;

        if (applyBasePriceEndDate === false) {
          total =
            hasTax === false ? programEarlyPrice + tax : programEarlyPrice;
        }

        if (userDiscount) {
          total = userDiscount.price;
        }

        return total;
      });

      return total.reduce((a, b) => a + b, 0);
    },

    interestAmount(state, getters) {
      if (state.totalWithInterest > 0) {
        return state.totalWithInterest - getters.cartTotal;
      } else {
        return 0.0;
      }
    },
  },

  mutations: {
    [ADD_ITEM_TO_CART](state, item) {
      const itemIndex = state.items.findIndex((el) => el.key == item.key);
      if (itemIndex > -1) {
        state.items[itemIndex].quantity = item.quantity;
      } else {
        state.items = [...state.items, item];
      }
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state.items));
    },
    [REMOVE_ITEM_FROM_CART](state, itemId) {
      state.items = state.items.filter((el) => el.key != itemId);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state.items));
    },
    [CLEAR_CART](state) {
      state.items = [];
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state.items));
    },

    [SET_FORWARD_URL](state, url) {
      state.forwardUrl = url;
    },

    [SET_TOTAL_WITH_INTEREST](state, amount) {
      state.totalWithInterest = amount;
    },
  },

  actions: {
    clearCart({ commit }) {
      commit(CLEAR_CART);
    },
    addCartItem({ commit }, payload) {
      commit(ADD_ITEM_TO_CART, payload);
    },
    removeCartItem({ commit, state }, cartItemId) {
      //! Check user periods in cart
      const [userId] = cartItemId.split("_");

      const item = state.items.find((el) => el.key == cartItemId);
      const userItems = state.items.filter((el) => el.key.includes(userId));

      if (item.type == "program") {
        const postPeriodCartItems = userItems.filter(
          (el) => el.data.idPeriodInt > item.data.idPeriodInt
        );

        //! Remove all post period items from cart
        for (const item of postPeriodCartItems) {
          commit(REMOVE_ITEM_FROM_CART, item.key);
        }

        const articlesInCart = userItems.filter(
          (el) =>
            el.type == "article" &&
            item.data.articleAgrupator
              .map((item) => item.articles)
              .flat()
              .map((art) => `${userId}_${art.idPeriodInt}_${art.idArticleInt}`)
              .includes(el.key)
        );

        //! Remove articles from deleted program
        for (const article of articlesInCart) {
          commit(REMOVE_ITEM_FROM_CART, article.key);
        }
      }

      //! Remove target item from cart
      commit(REMOVE_ITEM_FROM_CART, cartItemId);
    },
    addMultipleItemsToCart({ dispatch }, items) {
      for (const item of items) {
        dispatch("addCartItem", item);
      }
    },
    setForwardUrl({ commit }, url) {
      commit(SET_FORWARD_URL, url);
    },
    setTotalWithInterest({ commit }, amount) {
      commit(SET_TOTAL_WITH_INTEREST, amount);
    },
  },
};

export function generatePaymentChildsObject(rootState) {
  const shoppingCart = rootState.ShoppingCart.items;

  const childrenInCart = [
    ...new Set(shoppingCart.map((el) => parseInt(el.key.split("_")[0], 10))),
  ];

  return rootState.RelativeBalance.balance
    .filter((el) => childrenInCart.includes(el.idUserInt))
    .map((student) => {
      return {
        idUserInt: student.idUserInt,
        uuidUser: student.uuidUser,
        fullName: student.fullName,
        grado: student.grado,
        programItemPeriod: student.programItemPeriod
          .filter(
            //! Bring only periods in cart not bought or periods with articles in cart
            (item) =>
              isInShoopingCart(shoppingCart, item, student) ||
              hasArticlesInCart(shoppingCart, item, student)
          )
          .map((itemPeriod) => {
            const { childPrices, isProgramBought } = itemPeriod.program;
            return {
              articlesSelected: rootState.ShoppingCart.items
                .filter((el) => el.type == "article")
                .filter((item) =>
                  item.key.startsWith(
                    `${student.idUserInt}_${itemPeriod.idPeriodInt}`
                  )
                )
                .map((item) => ({
                  idArticleInt: item.data.idArticleInt,
                  name: item.data.name,
                  quantity: item.quantity,
                  price: item.data.price,
                })),
              program: {
                childPrices: {
                  name: childPrices.name,
                  agreementPrice: childPrices.agreementPrice,
                  idAgreementDetail: childPrices.idAgreementDetail,
                  userDiscount: childPrices.userDiscount,
                },
                isProgramBought,
              },
              hasProgramSelected: itemPeriod.hasProgramSelected,
              idPeriodInt: itemPeriod.idPeriodInt,
            };
          }),
        currentAgreement: {
          childPrices: {
            idAgreementDetail: student.currentAgreement
              ? student.currentAgreement.childPrices.idAgreementDetail
              : null,
          },
        },
      };
    });
}

function isInShoopingCart(shoppingCart, item, student) {
  return (
    !item.program.isProgramBought &&
    shoppingCart.find(
      (el) =>
        el.key ==
        `${student.idUserInt}_${item.program.childPrices.idAgreementDetailInt}`
    ) != null
  );
}

function hasArticlesInCart(shoppingCart, item, student) {
  return (
    shoppingCart.find((el) => {
      return item.articleAgrupator
        .map((group) => group.articles)
        .flat()
        .map(
          (art) => `${student.idUserInt}_${art.idPeriodInt}_${art.idArticleInt}`
        )
        .includes(el.key);
    }) != null
  );
}

export default relativeShoppingCartStore;
